/**
 * Web adapter for @tradeoutlet/data's KeyValueStore (docs/offline.md §5, §7.3): one IndexedDB database per user,
 * `hub-<userId>`, with the doc's `outbox` table. Plain IndexedDB (no library) for the one table we need so far.
 * If IndexedDB is missing or blocked (some private modes) every call rejects and the caller carries on in memory.
 */
import type { KeyValueStore } from '@tradeoutlet/data';

export const HUB_DB_VERSION = 1;
export const hubDbName = (userId: string) => `hub-${userId}`;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });
}

export function indexedDbStore<T>(dbName: string, table = 'outbox', factory: IDBFactory | null = globalThis.indexedDB ?? null): KeyValueStore<T> {
  let db: Promise<IDBDatabase> | null = null;
  const open = () => {
    if (!factory) return Promise.reject(new Error('IndexedDB unavailable'));
    db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const r = factory.open(dbName, HUB_DB_VERSION);
      r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(table)) r.result.createObjectStore(table); };
      r.onsuccess = () => { const d = r.result; d.onversionchange = () => { d.close(); db = null; }; resolve(d); };
      r.onerror = () => { db = null; reject(r.error); };
      r.onblocked = () => { db = null; reject(new Error('IndexedDB blocked')); };
    });
    return db;
  };
  const tx = async <R>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<R>): Promise<R> => {
    const d = await open();
    const t = d.transaction(table, mode);
    const result = req(f(t.objectStore(table)));
    await new Promise<void>((resolve, reject) => { t.oncomplete = () => resolve(); t.onerror = () => reject(t.error); t.onabort = () => reject(t.error); });
    return result;
  };
  return {
    getAll: () => tx('readonly', s => s.getAll() as IDBRequest<T[]>),
    put: async (k, v) => { await tx('readwrite', s => s.put(v, k)); },
    delete: async k => { await tx('readwrite', s => s.delete(k)); },
    clear: async () => { await tx('readwrite', s => s.clear()); },
  };
}
